// ---------- la signature des fichiers partagés : le logo et le nom ----------
// Une image ou une vidéo partagée (cf. src/sharePanel.ts et src/video.ts) circule SANS le jeu
// autour d'elle : transférée, enregistrée, revue des semaines plus tard, elle doit dire d'où elle
// vient. Une bande de papier sous la feuille, avec la marque et le mot « Codigrid » — demandé le
// 2026-10-16.
//
// À droite de la bande, le badge officiel « Disponible sur Google Play », dans la langue du joueur
// (ajouté le même jour) : qui reçoit le fichier doit comprendre que c'est une APPLICATION, et où
// la trouver. Ce sont les fichiers de public/badge/, ceux que la page de partage affiche déjà : tels
// que Google les fournit, marge de protection comprise — ses règles d'usage interdisent de les
// retoucher, on les pose donc tels quels.
//
// La marque est CELLE DE L'EN-TÊTE : le petit renard détouré de public/icons/marque.webp, le
// fichier que le joueur voit en haut du catalogue. C'était jusqu'au 2026-10-30 la diagonale des
// « Motifs », reconstruite ici en SVG autonome ; le renard est une image générée, il n'a pas de
// tracé à recopier. Ses 128 px couvrent la bande, où il fait ~75 px sur une image de 1080.

import { currentLang } from './i18n';

/** L'accent profond, lu sur la page : le seul de la charte qui se lise sur le papier crème. */
function accentDeep(): string {
  const rgb = getComputedStyle(document.documentElement).getPropertyValue('--acc-deep-rgb').trim();
  return rgb ? `rgb(${rgb})` : '#4a2faa';
}

/** Ce qui dessine la signature, une fois la marque, le badge et la police prêts : la marque et le
 *  nom calés à gauche sur `x0`, le badge calé à droite sur `x1`, le tout centré sur `cy`. */
export type BrandPainter = (
  g: CanvasRenderingContext2D,
  x0: number,
  x1: number,
  cy: number,
  h: number,
) => void;

let painter: Promise<BrandPainter> | null = null;

/**
 * Attend `p`, mais pas plus de `ms` : au-delà, rejette, et dit dans le journal QUELLE étape a
 * calé — `what`, lu dans logcat sur l'APK.
 *
 * LE BLOCAGE À 30 % (2026-10-22). Le partage d'une image restait parfois figé sur l'anneau de
 * préparation, sans erreur, jusqu'au redémarrage de l'application. Aucune des attentes de la
 * préparation n'avait de limite — décodage des images, chargement de la police, encodage du PNG —
 * et une seule qui ne répond jamais suffit à tout figer. Le cas ne s'est pas reproduit dans Chrome
 * de bureau (chaque étape sous 130 ms, au premier plan comme en arrière-plan) : c'est la WebView du
 * téléphone, où `HTMLImageElement.decode()` est connu pour rester parfois sans réponse. L'étape
 * exacte n'est pas identifiée ; ce journal est là pour qu'un prochain essai la nomme.
 */
export function withTimeout<T>(p: Promise<T>, ms: number, what: string): Promise<T> {
  let timer = 0;
  const late = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      console.warn(`partage : ${what}, sans réponse après ${ms} ms`);
      reject(new Error(`${what} : délai dépassé`));
    }, ms) as unknown as number;
  });
  return Promise.race([p, late]).finally(() => clearTimeout(timer));
}

/**
 * Charge une image, par ses événements `load` / `error` et non par `decode()` — c'est `decode()`
 * qu'on soupçonne de ne jamais répondre dans certaines WebView (cf. withTimeout) — et dans un délai.
 */
export function loadImage(src: string, ms: number, what: string): Promise<HTMLImageElement> {
  const img = new Image();
  const loaded = new Promise<HTMLImageElement>((resolve, reject) => {
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`${what} : illisible`));
  });
  img.src = src;
  return withTimeout(loaded, ms, what);
}

/**
 * Prépare la signature : la marque décodée, et la police Fredoka chargée — sans elle, `fillText`
 * écrirait le nom dans la police de repli, et le mot ne ressemblerait plus au logo du jeu. Mémorisé :
 * la marque ne se décode qu'une fois par session.
 *
 * RIEN ICI NE PEUT BLOQUER LE PARTAGE (2026-10-22, cf. withTimeout). Chaque attente a sa limite et
 * son repli : sans la marque, le nom seul ; sans le badge, pas de badge ; sans la police, celle de
 * repli. Une signature incomplète vaut mieux qu'un partage qui ne part jamais — et la promesse
 * mémorisée ne peut donc plus échouer, ce qui condamnait jusqu'ici, pour toute la session, tous les
 * partages suivants, image comme vidéo.
 */
export function brandPainter(): Promise<BrandPainter> {
  return (painter ??= (async () => {
    const acc = accentDeep();
    // Même origine que la page, comme le badge : le canvas qui la reçoit reste lisible.
    const img = await loadImage('icons/marque.webp', 3000, 'la marque').catch(() => null);
    // Le badge : un chemin RELATIF, servi par Vite comme par l'APK (public/ est copié à la racine).
    // Même origine que la page, donc le canvas qui le reçoit reste lisible pour toBlob et la vidéo.
    const badge = await loadImage(
      `badge/google-play-${currentLang()}.png`,
      3000,
      'le badge Google Play',
    ).catch(() => null);
    await withTimeout(document.fonts.load("700 48px 'Fredoka'"), 2000, 'la police').catch(
      () => undefined,
    );
    return (g, x0, x1, cy, h) => {
      const size = Math.round(h * 0.62);
      const font = Math.round(h * 0.46);
      g.font = `700 ${font}px 'Fredoka', 'Nunito', system-ui, sans-serif`;
      if (img) g.drawImage(img, x0, cy - size / 2, size, size);
      g.fillStyle = acc;
      g.textBaseline = 'middle';
      g.textAlign = 'left';
      g.fillText('Codigrid', x0 + size + Math.round(size * 0.3), cy + font * 0.04);
      if (!badge) return;
      // Le badge à la hauteur de la bande : son fichier porte déjà sa marge de protection, qui
      // l'aligne visuellement sur la marque. Sa largeur suit son propre rapport, jamais déformé.
      const bh = h * 0.95;
      const bw = (badge.width / badge.height) * bh;
      g.drawImage(badge, x1 - bw, cy - bh / 2, bw, bh);
    };
  })());
}

/** La hauteur de la bande de signature pour une largeur donnée. */
export function brandBand(width: number): number {
  return Math.round(width * 0.11);
}
